import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { newsCardsApi, newsApi } from '../lib/api';
import toast from 'react-hot-toast';
import { ArrowLeft, Save } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const NewsCardForm: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const queryClient = useQueryClient();
  const isEdit = Boolean(id);

  const [formData, setFormData] = useState({
    title_en: '',
    title_ar: '',
    summary_en: '',
    summary_ar: '',
    news_id: '',
  });

  const { data: newsCard, isLoading } = useQuery({
    queryKey: ['news-cards', id],
    queryFn: () => newsCardsApi.getById(id!).then((res: any) => res.data),
    enabled: isEdit,
  });

  const { data: news = [] } = useQuery({
    queryKey: ['news'],
    queryFn: () => newsApi.getAll().then((res: any) => res.data),
  });

  useEffect(() => {
    if (newsCard && isEdit) {
      setFormData({
        title_en: newsCard.title_en || '',
        title_ar: newsCard.title_ar || '',
        summary_en: newsCard.summary_en || '',
        summary_ar: newsCard.summary_ar || '',
        news_id: newsCard.news_id || '',
      });
    }
  }, [newsCard, isEdit]);

  const createMutation = useMutation({
    mutationFn: (data: any) => newsCardsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['news-cards'] });
      toast.success(t('newsCardCreated'));
      navigate('/news-cards');
    },
    onError: () => {
      toast.error(t('errorOccurred'));
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: any) => newsCardsApi.update(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['news-cards'] });
      toast.success(t('newsCardUpdated'));
      navigate('/news-cards');
    },
    onError: () => {
      toast.error(t('errorOccurred'));
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isEdit) {
      updateMutation.mutate(formData);
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleNewsChange = (value: string) => {
    setFormData(prev => ({
      ...prev,
      news_id: value,
    }));
  };

  if (isLoading && isEdit) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600">{t('loading')}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-4 rtl:space-x-reverse">
        <Button variant="outline" onClick={() => navigate('/news-cards')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          {i18n.language === 'ar' ? 'العودة' : 'Back'}
        </Button>
        <h1 className="text-3xl font-bold">
          {isEdit 
            ? (i18n.language === 'ar' ? 'تعديل كرت الأخبار' : 'Edit News Card')
            : (i18n.language === 'ar' ? 'إضافة كرت أخبار جديد' : 'Add New News Card')
          }
        </h1>
      </div>

      <form onSubmit={handleSubmit} className={`space-y-6 ${i18n.language === 'ar' ? 'rtl' : 'ltr'}`}>
        {/* Basic Information */}
        <Card>
          <CardHeader>
            <CardTitle>{i18n.language === 'ar' ? 'المعلومات الأساسية' : 'Basic Information'}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="title_en">{i18n.language === 'ar' ? 'العنوان (إنجليزي)' : 'Title (English)'}</Label>
                <Input
                  id="title_en"
                  name="title_en"
                  value={formData.title_en}
                  onChange={handleChange}
                  required
                  dir="ltr"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="title_ar">{i18n.language === 'ar' ? 'العنوان (عربي)' : 'Title (Arabic)'}</Label>
                <Input
                  id="title_ar"
                  name="title_ar"
                  value={formData.title_ar}
                  onChange={handleChange}
                  required
                  dir="rtl"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Summary */}
        <Card>
          <CardHeader>
            <CardTitle>{i18n.language === 'ar' ? 'الملخص' : 'Summary'}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="summary_en">{i18n.language === 'ar' ? 'الملخص (إنجليزي)' : 'Summary (English)'}</Label>
                <Textarea
                  id="summary_en"
                  name="summary_en"
                  value={formData.summary_en}
                  onChange={handleChange}
                  rows={4}
                  dir="ltr"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="summary_ar">{i18n.language === 'ar' ? 'الملخص (عربي)' : 'Summary (Arabic)'}</Label>
                <Textarea
                  id="summary_ar"
                  name="summary_ar"
                  value={formData.summary_ar}
                  onChange={handleChange}
                  rows={4}
                  dir="rtl"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* News Selection */}
        <Card>
          <CardHeader>
            <CardTitle>{i18n.language === 'ar' ? 'الخبر المرتبط' : 'Related News'}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Label htmlFor="news_id">{i18n.language === 'ar' ? 'اختر الخبر' : 'Select News'}</Label>
              <Select value={formData.news_id} onValueChange={handleNewsChange}>
                <SelectTrigger dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}>
                  <SelectValue placeholder={i18n.language === 'ar' ? 'اختر خبر...' : 'Select news...'} />
                </SelectTrigger>
                <SelectContent dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}>
                  {news.map((newsItem: any) => (
                    <SelectItem key={newsItem.id} value={newsItem.id.toString()}>
                      {i18n.language === 'ar' ? (newsItem.title_ar || newsItem.title) : (newsItem.title || newsItem.title_ar)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-sm text-gray-500">
                {i18n.language === 'ar' 
                  ? 'اختياري - يمكن ربط الكرت بخبر محدد'
                  : 'Optional - Link the card to a specific news item'
                }
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Submit Button */}
        <div className="flex justify-end">
          <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
            <Save className="h-4 w-4 mr-2" />
            {createMutation.isPending || updateMutation.isPending
              ? (i18n.language === 'ar' ? 'جاري الحفظ...' : 'Saving...')
              : (isEdit 
                ? (i18n.language === 'ar' ? 'تحديث كرت الأخبار' : 'Update News Card')
                : (i18n.language === 'ar' ? 'إنشاء كرت الأخبار' : 'Create News Card')
              )
            }
          </Button>
        </div>
      </form>
    </div>
  );
};

export default NewsCardForm;
